import type { GetUsersResult } from '@findigs/api';
import * as Sentry from '@sentry/remix';
import { createContext, useContext, useEffect, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import type {
  AdvancedUserModel,
  PermissionModel,
  UserRoleModel,
} from '~/types';

type UserContextProps = {
  user?: AdvancedUserModel;
  isDevUser?: boolean;
  userRole?: UserRoleModel | null;
  users?: GetUsersResult;
  userRoles?: UserRoleModel[];
  rightsList?: PermissionModel[];
  canApprove: boolean;
  canDeny: boolean;
  canCancel: boolean;
  canUndoApproval: boolean;
  canUndoDecline: boolean;
  canUndoCancel: boolean;
  canUndoPass: boolean;
};

type UserContextProvidedProps = Pick<
  UserContextProps,
  'user' | 'userRole' | 'users' | 'userRoles' | 'rightsList' | 'isDevUser'
>;

const UserContext = createContext<UserContextProps | null>(null);

export function useUserContext() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error(
      'useUserContext must be used within <UserContextProvider />'
    );
  }
  return context;
}

export function UserContextProvider({
  user,
  userRole,
  users,
  userRoles,
  rightsList,
  isDevUser = false,
  children,
}: PropsWithChildren<UserContextProvidedProps>) {
  const canApprove = useMemo(() => {
    return Boolean(userRole?.rights.can_approve);
  }, [userRole]);

  const canDeny = useMemo(() => {
    return Boolean(userRole?.rights.can_deny);
  }, [userRole]);

  const canCancel = useMemo(() => {
    return Boolean(userRole?.rights.can_cancel);
  }, [userRole]);

  const canUndoApproval = useMemo(() => {
    return Boolean(userRole?.rights.can_undo_approval);
  }, [userRole]);

  const canUndoDecline = useMemo(() => {
    return Boolean(userRole?.rights.can_undo_decline);
  }, [userRole]);

  const canUndoCancel = useMemo(() => {
    return Boolean(userRole?.rights.can_undo_cancel);
  }, [userRole]);

  const canUndoPass = useMemo(() => {
    return Boolean(userRole?.rights.can_undo_pass);
  }, [userRole]);

  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({
      id: user.pk,
      email: user.email,
      username: `${user.first_name} ${user.last_name}`.trim(),
    });
  }, [user]);

  const value = {
    user,
    userRole,
    users,
    userRoles: userRoles?.sort((a, b) => a.name.localeCompare(b.name)),
    rightsList,
    isDevUser,
    canApprove,
    canDeny,
    canCancel,
    canUndoApproval,
    canUndoDecline,
    canUndoCancel,
    canUndoPass,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
